import { useAuthStore } from '@/stores/authStore';
import { authService } from '@/services/authService';

/**
 * Hook personnalisé pour l'authentification
 * Fournit l'accès à l'état d'authentification et aux méthodes
 */
export function useAuth() {
  const {
    user,
    session,
    profile,
    isLoading,
    isAuthenticated,
    currentRestaurant,
    restaurants,
  } = useAuthStore();

  // L'ID du restaurant actuellement sélectionné (préférer currentRestaurant.id sur profile.restaurant_id)
  const currentRestaurantId = currentRestaurant?.id ?? profile?.restaurant_id ?? null;

  /**
   * Connexion avec email et password
   */
  const login = async (email: string, password: string) => {
    const result = await authService.login({ email, password });
    if (!result.success) {
      throw new Error(result.error || 'Erreur de connexion');
    }
    return result;
  };

  /**
   * Inscription avec création de restaurant
   */
  const signup = async (email: string, password: string, restaurantName: string) => {
    const result = await authService.signup({ email, password, restaurantName });
    if (!result.success) {
      throw new Error(result.error || 'Erreur d\'inscription');
    }
    return result;
  };

  /**
   * Déconnexion
   */
  const logout = async () => {
    const result = await authService.logout();
    if (!result.success) {
      throw new Error(result.error || 'Erreur de déconnexion');
    }
    return result;
  };

  /**
   * Vérifier si l'utilisateur a un rôle spécifique
   */
  const hasRole = (role: string | string[]) => {
    if (!profile) return false;
    if (Array.isArray(role)) {
      return role.includes(profile.role);
    }
    return profile.role === role;
  };

  /**
   * Vérifier si l'utilisateur est admin
   */
  const isAdmin = () => hasRole('admin');

  /**
   * Vérifier si l'utilisateur peut gérer le restaurant
   */
  const canManage = () => hasRole(['admin', 'manager']);

  /**
   * Mettre à jour les paramètres utilisateur
   */
  const updateUserSettings = async (settings: {
    onboarding_completed?: boolean;
    restaurant_type?: string;
    display_name?: string;
  }) => {
    return authService.updateUserSettings(settings);
  };

  return {
    // État
    user,
    session,
    profile,
    isLoading,
    isAuthenticated,

    // Multi-restaurants
    currentRestaurant,
    currentRestaurantId,
    restaurants,

    // Méthodes
    login,
    signup,
    logout,

    // Permissions
    hasRole,
    isAdmin,
    canManage,

    // Settings
    updateUserSettings,
  };
}
